import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Col, Row, Card, CardBody, Badge, Button, Modal, ModalHeader, ModalBody,
  ModalFooter, Spinner, Input, Alert, Nav, NavItem, NavLink, TabContent, TabPane,
  FormGroup, Label
} from "reactstrap";
import classnames from "classnames";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { getCarriers, createShipment } from "../../../services/shipEntegraService";

const BASE_URL = "https://harmanagelapi.stechomeyazilim.info";

const DURUM_RENKLERI = {
  "Beklemede": "warning",
  "Onaylandı": "info",
  "Kargoya Verildi": "primary",
  "Teslim Edildi": "success",
  "İptal Edildi": "danger",
  "İade Edildi": "secondary",
};

const SiparisYonetimi = () => {
  document.title = "Sipariş Yönetimi | HarmanaGEL";

  const [siparisler, setSiparisler] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");
  const [activeTab, setActiveTab] = useState("tumu");

  const [detailModal, setDetailModal] = useState(false);
  const [selectedSiparis, setSelectedSiparis] = useState(null);
  const [newStatus, setNewStatus] = useState("");

  // ShipEntegra kargo oluştur
  const [shipModal, setShipModal] = useState(false);
  const [carriers, setCarriers] = useState([]);
  const [carriersLoading, setCarriersLoading] = useState(false);
  const [shipForm, setShipForm] = useState({
    carrierId: "", receiverName: "", receiverPhone: "",
    receiverAddress: "", receiverCity: "", receiverDistrict: "",
    receiverPostalCode: "", weight: "1", desi: "1",
    description: "", paymentType: "0",
  });
  const [shipLoading, setShipLoading] = useState(false);

  const fetchCarriers = useCallback(async () => {
    if (carriers.length > 0) return;
    setCarriersLoading(true);
    try {
      const list = await getCarriers();
      setCarriers(list);
    } catch {
      setError("Kargo firmaları yüklenemedi.");
    } finally {
      setCarriersLoading(false);
    }
  }, [carriers.length]);

  const openShipModal = (siparis) => {
    setSelectedSiparis(siparis);
    const u = siparis.KULLANICILAR;
    setShipForm({
      carrierId: "", receiverName: u?.AD_SOYAD || "", receiverPhone: u?.CEP_NO || "",
      receiverAddress: "", receiverCity: "", receiverDistrict: "",
      receiverPostalCode: "", weight: "1", desi: "1",
      description: `Sipariş #${siparis.ID}`, paymentType: "0",
    });
    setShipModal(true);
    fetchCarriers();
  };

  const handleCreateShipment = async () => {
    if (!shipForm.carrierId) { setError("Lütfen kargo firması seçin."); return; }
    setShipLoading(true);
    try {
      const payload = {
        carrierId: shipForm.carrierId,
        receiver: {
          name: shipForm.receiverName, phone: shipForm.receiverPhone,
          address: shipForm.receiverAddress, city: shipForm.receiverCity,
          district: shipForm.receiverDistrict, postalCode: shipForm.receiverPostalCode,
        },
        package: { weight: parseFloat(shipForm.weight) || 1, desi: parseFloat(shipForm.desi) || 1 },
        description: shipForm.description,
        paymentType: parseInt(shipForm.paymentType, 10),
        referenceNo: `SIPARIS-${selectedSiparis.ID}`,
      };
      const result = await createShipment(payload);

      // Sipariş durumunu güncelle
      await fetch(`${BASE_URL}/SIPARIS(${selectedSiparis.ID})`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ SIPARIS_DURUMU: "Kargoya Verildi", KARGO_NO: result.barcode || result.trackingNumber || "" }),
      });

      setSuccessMsg(`Kargo oluşturuldu! Takip No: ${result.barcode || result.trackingNumber || "-"}`);
      setShipModal(false);
      fetchSiparisler();
      setTimeout(() => setSuccessMsg(""), 4000);
    } catch (err) {
      setError("Kargo oluşturma hatası: " + err.message);
    } finally {
      setShipLoading(false);
    }
  };

  const fetchSiparisler = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${BASE_URL}/SIPARIS?$top=500&$orderby=SIPARIS_TARIHI desc&$expand=KULLANICILAR($select=AD_SOYAD,Mail_Adress,CEP_NO),SIPARIS_DETAY`
      );
      const result = await response.json();
      if (result && result.value) setSiparisler(result.value);
    } catch (err) {
      setError("Siparişler yüklenirken hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSiparisler();
  }, []);

  const filteredSiparisler = useMemo(() => {
    if (activeTab === "tumu") return siparisler;
    return siparisler.filter(s => s.SIPARIS_DURUMU === activeTab);
  }, [siparisler, activeTab]);

  const handleUpdateStatus = async () => {
    if (!newStatus || !selectedSiparis) return;
    try {
      await fetch(`${BASE_URL}/SIPARIS(${selectedSiparis.ID})`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ SIPARIS_DURUMU: newStatus }),
      });
      setSuccessMsg("Sipariş durumu güncellendi.");
      setDetailModal(false);
      fetchSiparisler();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      setError("Güncelleme sırasında hata oluştu.");
    }
  };

  const statusCounts = useMemo(() => {
    const counts = {};
    siparisler.forEach(s => {
      counts[s.SIPARIS_DURUMU] = (counts[s.SIPARIS_DURUMU] || 0) + 1;
    });
    return counts;
  }, [siparisler]);

  const columns = useMemo(() => [
    {
      header: "Sipariş No",
      accessorKey: "ID",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => <span className="fw-bold text-primary">#{cell.getValue()}</span>,
    },
    {
      header: "Müşteri",
      accessorKey: "KULLANICILAR",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => {
        const u = cell.getValue();
        return u ? (
          <div>
            <div className="fw-medium">{u.AD_SOYAD}</div>
            <small className="text-muted">{u.Mail_Adress}</small>
          </div>
        ) : <span className="text-muted">ID: {cell.row.original.KULLANICI_ID}</span>;
      },
    },
    {
      header: "Tutar",
      accessorKey: "TOPLAM_TUTAR",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => (
        <span className="fw-bold text-success">
          {parseFloat(cell.getValue() || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺
        </span>
      ),
    },
    {
      header: "Durum",
      accessorKey: "SIPARIS_DURUMU",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => (
        <Badge color={DURUM_RENKLERI[cell.getValue()] || "secondary"}>
          {cell.getValue() || "Bilinmiyor"}
        </Badge>
      ),
    },
    {
      header: "Sipariş Tarihi",
      accessorKey: "SIPARIS_TARIHI",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => {
        const d = cell.getValue();
        return d ? new Date(d).toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-";
      },
    },
    {
      header: "Ürün Adedi",
      accessorKey: "SIPARIS_DETAY",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => {
        const detay = cell.getValue();
        return <Badge color="light" className="text-dark">{Array.isArray(detay) ? detay.length : 0} ürün</Badge>;
      },
    },
    {
      header: "İşlemler",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cellProps) => {
        const s = cellProps.row.original;
        return (
          <div className="d-flex gap-1">
            <Button color="primary" size="sm" outline onClick={() => {
              setSelectedSiparis(s);
              setNewStatus(s.SIPARIS_DURUMU);
              setDetailModal(true);
            }}>
              <i className="bx bx-edit me-1" />Detay
            </Button>
            {(s.SIPARIS_DURUMU === "Onaylandı" || s.SIPARIS_DURUMU === "Beklemede") && (
              <Button color="success" size="sm" onClick={() => openShipModal(s)}>
                <i className="bx bx-package me-1" />Kargoya Ver
              </Button>
            )}
          </div>
        );
      },
    },
  ], []);

  const tabItems = [
    { key: "tumu", label: "Tümü", count: siparisler.length },
    { key: "Beklemede", label: "Beklemede", count: statusCounts["Beklemede"] || 0 },
    { key: "Onaylandı", label: "Onaylandı", count: statusCounts["Onaylandı"] || 0 },
    { key: "Kargoya Verildi", label: "Kargoya Verildi", count: statusCounts["Kargoya Verildi"] || 0 },
    { key: "Teslim Edildi", label: "Teslim Edildi", count: statusCounts["Teslim Edildi"] || 0 },
    { key: "İptal Edildi", label: "İptal Edildi", count: statusCounts["İptal Edildi"] || 0 },
  ];

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="HarmanaGEL" breadcrumbItem="Sipariş Yönetimi" />

          {successMsg && <Alert color="success">{successMsg}</Alert>}
          {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

          {/* Özet Kartlar */}
          <Row className="mb-3">
            {[
              { label: "Toplam Sipariş", count: siparisler.length, color: "primary", icon: "bx-cart" },
              { label: "Beklemede", count: statusCounts["Beklemede"] || 0, color: "warning", icon: "bx-time" },
              { label: "Teslim Edildi", count: statusCounts["Teslim Edildi"] || 0, color: "success", icon: "bx-check-circle" },
              { label: "İptal Edildi", count: statusCounts["İptal Edildi"] || 0, color: "danger", icon: "bx-x-circle" },
            ].map((item, idx) => (
              <Col md={3} key={idx}>
                <Card className="mini-stats-wid">
                  <CardBody>
                    <div className="d-flex">
                      <div className="flex-grow-1">
                        <p className="text-muted fw-medium">{item.label}</p>
                        <h4 className="mb-0">{item.count}</h4>
                      </div>
                      <div className={`avatar-sm align-self-center mini-stat-icon rounded-circle bg-${item.color}`}>
                        <span className="avatar-title"><i className={`bx ${item.icon} font-size-24`} /></span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody className="pt-0 pb-0 border-bottom">
                  <Nav tabs className="nav-tabs-custom">
                    {tabItems.map(tab => (
                      <NavItem key={tab.key}>
                        <NavLink
                          className={classnames({ active: activeTab === tab.key })}
                          onClick={() => setActiveTab(tab.key)}
                          style={{ cursor: "pointer" }}
                        >
                          {tab.label}
                          {tab.count > 0 && (
                            <Badge color="primary" pill className="ms-1">{tab.count}</Badge>
                          )}
                        </NavLink>
                      </NavItem>
                    ))}
                  </Nav>
                </CardBody>
                <CardBody>
                  {isLoading ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" />
                    </div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={filteredSiparisler}
                      isGlobalFilter={true}
                      SearchPlaceholder="Sipariş ara..."
                      isPagination={true}
                      isCustomPageSize={true}
                      tableClass="table-bordered align-middle nowrap w-100 mb-0"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      {/* ShipEntegra Kargo Oluştur Modal */}
      <Modal isOpen={shipModal} toggle={() => setShipModal(false)} size="lg" centered>
        <ModalHeader toggle={() => setShipModal(false)}>
          <i className="bx bxs-package me-2 text-success" />
          ShipEntegra ile Kargoya Ver — Sipariş #{selectedSiparis?.ID}
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md={6}>
              <h6 className="mb-3 text-muted">Kargo Ayarları</h6>
              <FormGroup>
                <Label>Kargo Firması *</Label>
                {carriersLoading ? (
                  <div className="text-center py-2"><Spinner size="sm" /> Yükleniyor...</div>
                ) : (
                  <Input type="select" value={shipForm.carrierId}
                    onChange={(e) => setShipForm({ ...shipForm, carrierId: e.target.value })}>
                    <option value="">-- Seçin --</option>
                    {carriers.map((c, i) => (
                      <option key={i} value={c.id || c.Id || c.carrierId}>
                        {c.name || c.Name || c.carrierName || `Firma ${i + 1}`}
                      </option>
                    ))}
                  </Input>
                )}
              </FormGroup>
              <FormGroup>
                <Label>Ödeme Tipi</Label>
                <Input type="select" value={shipForm.paymentType}
                  onChange={(e) => setShipForm({ ...shipForm, paymentType: e.target.value })}>
                  <option value="0">Gönderici Öder</option>
                  <option value="1">Kapıda Ödeme</option>
                </Input>
              </FormGroup>
              <Row>
                <Col>
                  <FormGroup>
                    <Label>Ağırlık (kg)</Label>
                    <Input type="number" min="0.1" step="0.1" value={shipForm.weight}
                      onChange={(e) => setShipForm({ ...shipForm, weight: e.target.value })} />
                  </FormGroup>
                </Col>
                <Col>
                  <FormGroup>
                    <Label>Desi</Label>
                    <Input type="number" min="1" value={shipForm.desi}
                      onChange={(e) => setShipForm({ ...shipForm, desi: e.target.value })} />
                  </FormGroup>
                </Col>
              </Row>
              <FormGroup>
                <Label>Açıklama</Label>
                <Input value={shipForm.description}
                  onChange={(e) => setShipForm({ ...shipForm, description: e.target.value })} />
              </FormGroup>
            </Col>
            <Col md={6}>
              <h6 className="mb-3 text-muted">Alıcı Bilgileri</h6>
              <FormGroup>
                <Label>Ad Soyad *</Label>
                <Input value={shipForm.receiverName}
                  onChange={(e) => setShipForm({ ...shipForm, receiverName: e.target.value })} />
              </FormGroup>
              <FormGroup>
                <Label>Telefon *</Label>
                <Input value={shipForm.receiverPhone}
                  onChange={(e) => setShipForm({ ...shipForm, receiverPhone: e.target.value })} />
              </FormGroup>
              <FormGroup>
                <Label>Şehir *</Label>
                <Input value={shipForm.receiverCity}
                  onChange={(e) => setShipForm({ ...shipForm, receiverCity: e.target.value })}
                  placeholder="İstanbul" />
              </FormGroup>
              <FormGroup>
                <Label>İlçe</Label>
                <Input value={shipForm.receiverDistrict}
                  onChange={(e) => setShipForm({ ...shipForm, receiverDistrict: e.target.value })}
                  placeholder="Kadıköy" />
              </FormGroup>
              <FormGroup>
                <Label>Posta Kodu</Label>
                <Input value={shipForm.receiverPostalCode}
                  onChange={(e) => setShipForm({ ...shipForm, receiverPostalCode: e.target.value })}
                  placeholder="34000" />
              </FormGroup>
              <FormGroup>
                <Label>Adres *</Label>
                <Input type="textarea" rows={2} value={shipForm.receiverAddress}
                  onChange={(e) => setShipForm({ ...shipForm, receiverAddress: e.target.value })}
                  placeholder="Açık adres..." />
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setShipModal(false)} disabled={shipLoading}>İptal</Button>
          <Button color="success" onClick={handleCreateShipment} disabled={shipLoading}>
            {shipLoading ? <><Spinner size="sm" className="me-1" />Gönderiliyor...</> : <><i className="bx bx-package me-1" />Kargoyu Oluştur</>}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Sipariş Detay & Durum Güncelle Modal */}
      <Modal isOpen={detailModal} toggle={() => setDetailModal(false)} size="lg" centered>
        <ModalHeader toggle={() => setDetailModal(false)}>
          Sipariş #{selectedSiparis?.ID}
        </ModalHeader>
        <ModalBody>
          {selectedSiparis && (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <table className="table table-sm table-bordered">
                    <tbody>
                      <tr><th>Müşteri</th><td>{selectedSiparis.KULLANICILAR?.AD_SOYAD || `ID: ${selectedSiparis.KULLANICI_ID}`}</td></tr>
                      <tr><th>Toplam Tutar</th><td className="fw-bold text-success">{parseFloat(selectedSiparis.TOPLAM_TUTAR || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺</td></tr>
                      <tr><th>Sipariş Tarihi</th><td>{selectedSiparis.SIPARIS_TARIHI ? new Date(selectedSiparis.SIPARIS_TARIHI).toLocaleDateString("tr-TR") : "-"}</td></tr>
                      <tr><th>Kargo No</th><td>{selectedSiparis.KARGO_NO || "-"}</td></tr>
                      <tr><th>Sipariş Notu</th><td>{selectedSiparis.SIPARIS_NOTU || "-"}</td></tr>
                    </tbody>
                  </table>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label fw-medium">Mevcut Durum</label>
                    <div>
                      <Badge color={DURUM_RENKLERI[selectedSiparis.SIPARIS_DURUMU] || "secondary"} className="p-2 fs-6">
                        {selectedSiparis.SIPARIS_DURUMU}
                      </Badge>
                    </div>
                  </div>
                  <div>
                    <label className="form-label fw-medium">Durumu Güncelle</label>
                    <Input
                      type="select"
                      value={newStatus}
                      onChange={(e) => setNewStatus(e.target.value)}
                    >
                      <option value="Beklemede">Beklemede</option>
                      <option value="Onaylandı">Onaylandı</option>
                      <option value="Kargoya Verildi">Kargoya Verildi</option>
                      <option value="Teslim Edildi">Teslim Edildi</option>
                      <option value="İptal Edildi">İptal Edildi</option>
                      <option value="İade Edildi">İade Edildi</option>
                    </Input>
                  </div>
                </Col>
              </Row>

              {selectedSiparis.IPTAL_ACIKLAMASI && (
                <div className="alert alert-danger">
                  <strong>İptal Açıklaması:</strong> {selectedSiparis.IPTAL_ACIKLAMASI}
                </div>
              )}

              {Array.isArray(selectedSiparis.SIPARIS_DETAY) && selectedSiparis.SIPARIS_DETAY.length > 0 && (
                <>
                  <h6 className="mt-3 mb-2">Sipariş Kalemleri</h6>
                  <table className="table table-sm table-striped">
                    <thead className="table-light">
                      <tr>
                        <th>ID</th>
                        <th>Ürün ID</th>
                        <th>Adet</th>
                        <th>Birim Fiyat</th>
                        <th>Toplam</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedSiparis.SIPARIS_DETAY.map((d, i) => (
                        <tr key={i}>
                          <td>#{d.ID}</td>
                          <td>{d.ILANLAR_ID || d.URUN_ID || "-"}</td>
                          <td>{d.ADET || 1}</td>
                          <td>{parseFloat(d.BIRIM_FIYAT || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺</td>
                          <td className="fw-medium">{parseFloat(d.TOPLAM || d.BIRIM_FIYAT || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setDetailModal(false)}>Kapat</Button>
          <Button color="primary" onClick={handleUpdateStatus}>
            Durumu Güncelle
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default SiparisYonetimi;
